<script setup>
import { computed } from 'vue';

const props = defineProps({
    modelValue: { type: Object, default: () => ({ allowed_provinces: [] }) },
    errors: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue', 'touched']);

const v = computed({
    get: () => props.modelValue || { allowed_provinces: [] },
    set: (val) => emit('update:modelValue', val),
});

const provinces = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'ON', 'PE', 'QC', 'SK', 'YK'];

const selected = computed({
    get: () => (Array.isArray(v.value.allowed_provinces) ? v.value.allowed_provinces : []),
    set: (arr) => {
        v.value = { ...v.value, allowed_provinces: Array.isArray(arr) ? arr : [] };
    },
});

const isSelected = (code) => selected.value.includes(code);

const setSelected = (code, checked) => {
    emit('touched');
    const cur = new Set(selected.value);
    if (checked) {
        cur.add(code);
    } else {
        cur.delete(code);
    }
    selected.value = Array.from(cur);
};

const modelFor = (code) =>
    computed({
        get: () => isSelected(code),
        set: (checked) => setSelected(code, !!checked),
    });

const hasError = computed(() => Array.isArray(props.errors) && props.errors.length > 0);

</script>

<template>
<div class="w-full" :class="{ 'v-input--error': hasError }">
    <p class="font-semibold mb-2">{{ $t('label.acceptable_client_province') }}</p>

    <div class="province-grid">
        <StyledCheckbox
            v-for="code in provinces"
            :key="code"
            v-model="modelFor(code).value"
            :name="`provincial_acceptance.${code}`"
            :label="code"
            hide-details
            density="compact" />
    </div>

    <div
        v-if="hasError"
        class="v-input__details mt-2"
        role="alert"
        aria-live="polite">
        <div class="v-messages">
            <div
                v-for="err in errors"
                :key="err"
                class="v-messages__message">
                {{ err }}
            </div>
        </div>
    </div>
</div>
</template>

<style scoped>
.province-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;
}

@media (min-width: 768px) {
    .province-grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
        column-gap: 18px;
        row-gap: 6px;
    }
}

/* Kill extra Vuetify checkbox spacing */
.province-grid :deep(.v-input) {
    margin: 0;
}

.province-grid :deep(.v-selection-control) {
    min-height: 0;
}

.province-grid :deep(.v-input__details) {
    display: none;
}
</style>
